import { useCallback, useMemo, useState } from 'react'

import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import { useMissionsStore } from '@/shared/store/useMissionsStore'
import {
  effectiveFilter,
  groupByStatus,
  visibleFilterOptions
} from '@/shared/lib/list-view'
import { isRecent } from '@/shared/lib/recency'
import type { MissionWithProgress } from '@/types/mission'

import {
  MISSION_ENDING_SOON_HOURS,
  MISSION_SORT_PRIORITY,
  MISSION_STATUS_LABELS,
  type MissionFilterId,
  type MissionUiStatus
} from '../constants/status'
import {
  getMissionRewardScore,
  getMissionUiStatus,
  isMissionEndingSoon
} from '../lib/missionUiStatus'
import { matchesMissionFilter } from '../lib/missionFilterMatch'
import { MISSION_FILTER_OPTIONS } from '../lib/missionFilterOptions'
import { computeMissionRewardScoreboard } from '../lib/computeMissionRewardScoreboard'
import { selectFeaturedMission } from '../lib/selectFeaturedMission'

export type MissionListItem = MissionWithProgress & {
  uiStatus: MissionUiStatus
  /** Eixo independente de `uiStatus` — ver `shared/lib/recency`. */
  isNew: boolean
  isRecommended: boolean
  isEndingSoon: boolean
}

export const useMissionsListView = () => {
  const { visibleMissions } = useMissionsStore()
  const { version } = useGamificationDataStore(s => s.getActiveProductData())
  const [rawFilter, setFilter] = useState<MissionFilterId>('all')

  const activeMissions = useMemo(
    () => visibleMissions.filter(mission => !!mission.active),
    [visibleMissions]
  )

  const items = useMemo((): MissionListItem[] => {
    const recommendedIds = new Set(
      [...activeMissions]
        .filter(m => {
          const status = getMissionUiStatus(m)
          return status === 'available' || status === 'progress'
        })
        .sort((a, b) => getMissionRewardScore(b) - getMissionRewardScore(a))
        .slice(0, 3)
        .map(m => m.id)
    )

    /* Um único instante para a lista inteira — ver `shared/lib/recency`. */
    const now = Date.now()

    return activeMissions.map(mission => ({
      ...mission,
      uiStatus: getMissionUiStatus(mission),
      isNew: isRecent(mission.created_at, now),
      isRecommended: recommendedIds.has(mission.id),
      isEndingSoon: isMissionEndingSoon(
        mission,
        MISSION_ENDING_SOON_HOURS
      )
    }))
  }, [activeMissions])

  const visibleFilters = useMemo(
    () =>
      visibleFilterOptions(MISSION_FILTER_OPTIONS, id =>
        items.some(item => matchesMissionFilter(item, id))
      ),
    [items]
  )

  /* Derivação, não efeito — ver `shared/lib/list-view`. */
  const filter = effectiveFilter(rawFilter, visibleFilters)

  const sortedItems = useMemo(() => {
    const filtered = items.filter(item => matchesMissionFilter(item, filter))

    return [...filtered].sort((a, b) => {
      const priority =
        MISSION_SORT_PRIORITY[a.uiStatus] - MISSION_SORT_PRIORITY[b.uiStatus]

      if (priority !== 0) return priority

      return (a.position ?? 0) - (b.position ?? 0)
    })
  }, [items, filter])

  const featuredMission = useMemo(
    () => selectFeaturedMission(items),
    [items]
  )

  const globalProgress = useMemo(() => {
    const total = items.length
    const completed = items.filter(m => m.uiStatus === 'success').length
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0

    const incomplete = items.filter(
      m => m.uiStatus === 'available' || m.uiStatus === 'progress'
    )

    const nextMission = [...incomplete].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    )[0]

    const rewardScoreboard = computeMissionRewardScoreboard(items)

    return {
      completed,
      total,
      percent,
      rewardScoreboard,
      nextMission,
      remainingCount: nextMission ? 1 : 0
    }
  }, [items])

  /* Seções só em "Todas". O card em destaque é renderizado ACIMA das seções e
     por isso sai do agrupamento — sem `exclude` ele apareceria duas vezes. */
  const groups = useMemo(() => {
    if (filter !== 'all') return []

    const featuredId = featuredMission?.id

    return groupByStatus(items, {
      order: MISSION_SORT_PRIORITY,
      labels: MISSION_STATUS_LABELS,
      statusOf: item => item.uiStatus,
      exclude: item => !!featuredId && item.id === featuredId
    })
  }, [filter, items, featuredMission])

  return {
    filter,
    setFilter,
    items: sortedItems,
    allItems: items,
    visibleFilters,
    groups,
    featuredMission,
    globalProgress,
    isEmpty: items.length === 0,
    isFilteredEmpty: sortedItems.length === 0 && items.length > 0,
    isLoading: version === ''
  }
}
